<script lang="ts" setup>
import Content from "./Content.vue";
import { useRoute, useRouter } from "vue-router";
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import {
  LoadingSpinner,
  Tooltip,
  DocumentUploadModal,
  Dropdown,
  PromomatsImporter,
  UrlBasedDocumentImporter,
} from "@/components";
import {
  ChildWorkspaceFragment,
  type WorkspaceWithChildWorkspacesQuery,
  type WorkspaceWithChildWorkspacesQueryVariables,
  useWorkspaceWithChildWorkspacesQuery,
} from "@/graphql/operations";
import graphqlClient from "@/graphql/client";
import { useStore } from "vuex";
import WorkspaceBreadcrumbs from "./WorkspaceBreadcrumbs.vue";
import { useI18n } from "vue-i18n";
import { Cookie } from "@/util";
import { useFlashMessage } from "@/hooks";
import {
  ChildWorkspaceOrderRefreshDocument,
  type ChildWorkspaceOrderRefreshResult,
  useWorkspaceOrdering,
} from "@/composables/useWorkspaceOrdering";

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { displayFlashMessage } = useFlashMessage();

const emits = defineEmits(["contentLoaded"]);

const workspaceId = route.params.workspaceId as string;
type LoadedWorkspace = NonNullable<
  WorkspaceWithChildWorkspacesQuery["workspace"]
>;
const lastLoadedWorkspace = ref<LoadedWorkspace | null>(null);

/* fetch child workspaces */
const { fetching, executeQuery, data, error } =
  useWorkspaceWithChildWorkspacesQuery({
    variables: {
      id: workspaceId,
    },
    requestPolicy: "cache-and-network",
  });

watch(
  [data, error],
  ([newData, queryError]) => {
    if (!queryError && newData) {
      lastLoadedWorkspace.value = newData.workspace ?? null;
    }
  },
  { immediate: true },
);

watch(
  data,
  (newData, oldData) => {
    if (newData && !oldData) {
      emits("contentLoaded");
    }
  },
  { immediate: true },
);

const intervalId = setInterval(() => {
  executeQuery();
}, 6 * 1000);

onBeforeUnmount(() => {
  clearInterval(intervalId);
});

const workspace = computed(() => lastLoadedWorkspace.value);
const subfolders = computed(
  () =>
    workspace.value?.childWorkspaces?.nodes?.filter(
      (folder): folder is NonNullable<typeof folder> => folder != null,
    ) ?? [],
);
const workspaceOrder = useWorkspaceOrdering(subfolders, {
  refresh: async () => {
    await executeQuery({ requestPolicy: "network-only" });
    const result = await graphqlClient
      .query<
        ChildWorkspaceOrderRefreshResult,
        WorkspaceWithChildWorkspacesQueryVariables
      >(ChildWorkspaceOrderRefreshDocument, { id: workspaceId }, { requestPolicy: "network-only" })
      .toPromise();
    if (result.error) throw result.error;
    if (result.data?.workspace === null) return [];
    const nodes = result.data?.workspace?.childWorkspaces?.nodes;
    return nodes?.filter(
      (folder): folder is NonNullable<typeof folder> => folder != null,
    );
  },
});
const orderingExitTarget = ref<HTMLElement | null>(null);
const orderingExitFallback = ref<HTMLElement | null>(null);
watch(
  workspace,
  (currentWorkspace) => {
    if (!currentWorkspace) workspaceOrder.done();
  },
  { immediate: true },
);

const uploadPopupActive = ref<boolean>(false);
const promomatsImporterActive = ref(false);
const urlBasedImporterActive = ref(false);

const uploadActions = computed(() => {
  const items = [];
  if (!workspace.value?.myPrivileges?.mayAdministrate) {
    return [];
  }

  items.push({ title: t("workspace.uploadLocalDevice"), value: "upload" });

  if (store.getters.user.hasPromomatsImport()) {
    items.push({
      title: t("workspace.uploadFromPromomats"),
      value: "promomats_import",
    });
  }

  if (workspace.value.mayUseUrlBasedDocumentImport) {
    items.push({
      title: t("workspace.urlBasedUpload"),
      value: "url_based_import",
    });
  }

  return items;
});

function handleUploadAction(value: string) {
  switch (value) {
    case "upload":
      uploadPopupActive.value = true;
      break;

    case "promomats_import":
      promomatsImporterActive.value = true;
      break;

    case "url_based_import":
      urlBasedImporterActive.value = true;
      break;
  }
}

function handleUploadSuccess() {
  displayFlashMessage(t("workspace.uploadSuccess"), "success");
  executeQuery();
}

const layoutOptions = [
  {
    value: "list_view",
    title: t("workspace.layout.listView"),
  },
  {
    value: "grid_view",
    title: t("workspace.layout.gridView"),
  },
];

const currentLayout = ref(Cookie.get("workspace_layout") ?? "grid_view");
const changeLayout = (value: string) => {
  currentLayout.value = value;
  Cookie.set("workspace_layout", value);
};

const workspaceMenuItems = computed(() => {
  const items = [];
  if (workspaceOrder.eligible.value) {
    items.push({
      title: t("workspace.ordering.edit"),
      value: "edit-order",
    });
  }
  if (workspace.value?.myPrivileges?.mayAdministrate) {
    items.push({ title: t("workspace.details"), value: "details" });
  }
  return items;
});

const orderingStatusText = computed(() => {
  switch (workspaceOrder.status.value) {
    case "saving":
      return t("workspace.ordering.saving");
    case "saved":
      return t("workspace.ordering.saved");
    case "error":
      return t("workspace.ordering.error");
    default:
      return "";
  }
});

async function focusOrderingHandle(targetId?: string | null) {
  await nextTick();
  await nextTick();
  const scope = document.querySelector<HTMLElement>(
    '[data-test="workspace-page"]',
  );
  const target = targetId
    ? Array.from(
        scope?.querySelectorAll<HTMLElement>("[data-workspace-order-id]") ?? [],
      ).find((item) => item.dataset.workspaceOrderId === targetId)
    : null;
  (target ?? scope)
    ?.querySelector<HTMLButtonElement>(
      '[data-workspace-order-handle][aria-disabled="false"]',
    )
    ?.focus();
}

async function focusOrderingExitTarget() {
  await nextTick();
  await nextTick();
  const menu = document.querySelector<HTMLElement>(
    '[data-test="goto-workspace-settings-ctrl"]',
  );
  (menu ?? orderingExitTarget.value ?? orderingExitFallback.value)?.focus();
}

async function focusOrderingExitTargetIfLost() {
  await nextTick();
  await nextTick();
  if (document.activeElement === document.body) {
    await focusOrderingExitTarget();
  }
}

watch(workspaceOrder.editing, (editing, wasEditing) => {
  if (wasEditing && !editing) {
    void focusOrderingExitTargetIfLost();
  }
});

watch(workspaceOrder.eligible, (eligible, wasEligible) => {
  const menu = document.querySelector<HTMLElement>(
    '[data-test="goto-workspace-settings-ctrl"]',
  );
  const invalidatedFocusedMenu =
    wasEligible &&
    !eligible &&
    menu?.closest(".dropdown")?.contains(document.activeElement);
  if (invalidatedFocusedMenu) void focusOrderingExitTarget();
});

async function handleWorkspaceAction(action: string) {
  if (action === "edit-order") {
    if (workspaceOrder.start()) await focusOrderingHandle();
  } else if (action === "details") {
    router.push({ name: "WorkspaceDetail" });
  }
}

async function retryWorkspaceOrdering() {
  const targetId = workspaceOrder.failedTargetId.value;
  await focusOrderingHandle(targetId);
  await workspaceOrder.retry();
  await focusOrderingHandle(targetId);
}

async function finishWorkspaceOrdering() {
  if (!workspaceOrder.done()) return;
  await focusOrderingExitTarget();
}
</script>

<template>
  <div
    ref="orderingExitFallback"
    class="user_workspace page_content"
    data-test="workspace-page"
    tabindex="-1"
  >
    <div v-if="fetching && !workspace" class="empty_pane">
      <loading-spinner :color="'var(--classic-text, black)'" />
    </div>

    <div v-else-if="workspace">
      <div
        ref="orderingExitTarget"
        class="title_container"
        data-test="classic-child-order-exit-target"
        tabindex="-1"
      >
        <workspace-breadcrumbs />

        <div class="right">
          <dropdown
            :items="layoutOptions"
            class="right"
            data-test="workspace-layout-menu"
            :value="currentLayout"
            @change="changeLayout"
          >
            <tooltip class="bottom">
              <template v-slot>
                <span class="material-symbols-outlined icon clickable">
                  grid_view
                </span>
              </template>
              <template #tooltip>
                {{ $t("workspace.layout.tooltip") }}
              </template>
            </tooltip>
          </dropdown>

          <tooltip class="bottom" v-if="uploadActions.length === 1">
            <template v-slot>
              <button
                type="button"
                class="toolbar_icon_button"
                :aria-label="$t('workspace.upload')"
                data-test="upload-ctrl"
                @click="uploadPopupActive = true"
              >
                <span
                  class="material-symbols-outlined icon clickable"
                  aria-hidden="true"
                >
                  file_upload
                </span>
              </button>
            </template>
            <template #tooltip> {{ $t("workspace.upload") }} </template>
          </tooltip>
          <dropdown
            v-else-if="uploadActions.length > 1"
            class="right_align"
            @change="handleUploadAction"
            :items="uploadActions"
            :trigger-aria-label="$t('workspace.upload')"
          >
            <template v-slot>
              <span
                class="material-symbols-outlined icon clickable"
                data-test="upload-ctrl"
                aria-hidden="true"
              >
                file_upload
              </span>
            </template>
          </dropdown>

          <template v-if="workspaceOrder.editing.value">
            <span
              v-if="workspaceOrder.status.value !== 'idle'"
              data-test="workspace-order-status"
              class="ordering_status"
              :class="`is_${workspaceOrder.status.value}`"
              role="status"
            >
              {{ orderingStatusText }}
            </span>
            <button
              v-if="workspaceOrder.status.value === 'error'"
              data-test="workspace-order-retry"
              type="button"
              class="button ordering_retry"
              :disabled="workspaceOrder.saving.value"
              @click="retryWorkspaceOrdering"
            >
              {{ $t("workspace.ordering.retry") }}
            </button>
            <button
              type="button"
              class="button primary ordering_done"
              data-test="workspace-order-done"
              :disabled="
                workspaceOrder.saving.value ||
                workspaceOrder.gestureActive.value
              "
              @click="finishWorkspaceOrdering"
            >
              {{ $t("workspace.ordering.done") }}
            </button>
          </template>
          <dropdown
            v-else-if="workspaceOrder.eligible.value"
            class="right_align"
            :items="workspaceMenuItems"
            touch-target
            :trigger-aria-label="$t('workspace.actions')"
            trigger-data-test="goto-workspace-settings-ctrl"
            @change="handleWorkspaceAction"
          >
            <tooltip class="bottom">
              <template v-slot>
                <span class="toolbar_icon_button" aria-hidden="true">
                  <span class="material-symbols-outlined icon clickable">
                    more_vert
                  </span>
                </span>
              </template>
              <template #tooltip> {{ $t("workspace.actions") }} </template>
            </tooltip>
          </dropdown>
          <tooltip
            v-else-if="workspace.myPrivileges?.mayAdministrate"
            class="bottom"
          >
            <template v-slot>
              <router-link
                :to="{ name: 'WorkspaceDetail' }"
                class="workspace_details_link"
                data-test="goto-workspace-settings-ctrl"
                :aria-label="$t('workspace.details')"
              >
                <span class="material-symbols-outlined icon clickable">
                  more_vert
                </span>
              </router-link>
            </template>
            <template #tooltip> {{ $t("workspace.details") }} </template>
          </tooltip>
        </div>
      </div>

      <Content
        :layout="currentLayout"
        :subfolders="workspaceOrder.items.value as ChildWorkspaceFragment[]"
        :subfolder-ordering="workspaceOrder.editing.value"
        :subfolder-ordering-disabled="workspaceOrder.saving.value"
        @move-subfolder="workspaceOrder.move"
        @subfolder-ordering-gesture="workspaceOrder.setGestureActive"
      />
    </div>

    <div v-else class="empty_pane info">
      <span class="text">{{ $t("workspace.notFound") }}</span>
    </div>
  </div>

  <DocumentUploadModal
    v-if="uploadPopupActive"
    :active="uploadPopupActive"
    @close="uploadPopupActive = false"
    @success="handleUploadSuccess"
  >
  </DocumentUploadModal>

  <PromomatsImporter
    v-if="promomatsImporterActive"
    @close="promomatsImporterActive = false"
    @success="handleUploadSuccess"
  />

  <UrlBasedDocumentImporter
    v-if="urlBasedImporterActive"
    @close="urlBasedImporterActive = false"
    @success="handleUploadSuccess"
  />
</template>

<style lang="scss" scoped>
.user_workspace .empty_pane {
  height: calc(100vh - 40px - 2em);
}

.info {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.info .text {
  opacity: 0.8;
}

.title_container {
  display: flex;
  align-items: center;
  margin-bottom: 0.8em;
}

.ordering_status {
  color: var(--classic-text-secondary, #666);
  font-size: 0.82rem;
  white-space: nowrap;
}

.ordering_status.is_saved {
  color: var(--classic-success, #27844a);
}

.ordering_status.is_error {
  color: var(--classic-danger, #cf453e);
}

.ordering_retry,
.ordering_done {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.title_container > .right {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.toolbar_icon_button {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  font: inherit;
  line-height: inherit;
  margin: 0;
  padding: 0;
}

.icon.material-symbols-outlined {
  color: $color-secondary;
  height: 1.5em;
  width: 1.5em;
  text-align: center;
  padding-top: 0.18em;
  box-sizing: border-box;
  border: 1px solid $color-secondary;
  border-radius: 50%;
  margin-left: 0.2em;
  font-size: 24px;
}

.workspace_details_link {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  text-decoration: none;
}

.workspace_details_link:focus-visible {
  border-radius: 4px;
  outline: 2px solid var(--classic-accent, #34b5ba);
  outline-offset: 2px;
}

@media (max-width: 767px) {
  .title_container {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .title_container > .right {
    flex-wrap: wrap;
    gap: 0.35rem;
    justify-content: flex-end;
    margin-left: 0;
    width: 100%;
  }

  .ordering_status {
    overflow-wrap: anywhere;
    white-space: normal;
  }
}

@media (max-width: 767px), (any-pointer: coarse) {
  .ordering_retry,
  .ordering_done,
  .workspace_details_link {
    min-height: 44px;
    min-width: 44px;
  }
}
</style>
